import {ref, watch} from "vue";
import {AxiosError} from "axios";
import {aiAssistantApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {invalidatePlanningRange} from "@/planning/composables/usePlanningRange";
import {Proposition} from "@/ai-assistant/interfaces";
import {CANCEL_ERRORS, DECISION_GENERIC_ERROR} from "@/ai-assistant/constants";

/**
 * Bumped whenever a decision is taken outside this panel.
 *
 * The history sits above the conversation and both are visible at once, so a
 * scenario applied from a card below would otherwise leave the row above
 * reading « Proposé » — and offering no undo for the change just made.
 */
const revision = ref<number>(0);

/** Tell a mounted history that a decision was taken elsewhere. */
export const invalidateAssistantHistory = (): void => {
    revision.value++;
};

/**
 * The propositions this pharmacy has produced, and the undo of an applied one.
 *
 * Read from the propositions rather than from a transcript: nothing archives
 * the conversation in this version, while every decision is persisted with its
 * state, its versions and, since the pharmacist clicked it, its author. That is
 * what makes a history deliverable at all.
 */
const useAssistantHistory = () => {
    const organizationId = useOrganizationId();

    const propositions = ref<Proposition[]>([]);
    const loading = ref<boolean>(false);
    const failed = ref<boolean>(false);
    const cancelling = ref<number | null>(null);
    const failure = ref<string | null>(null);

    const page = ref<number>(1);
    const lastPage = ref<number>(1);

    /** Whether anything older than what is shown remains to be read. */
    const hasMore = ref<boolean>(false);

    const fetchPage = async (wanted: number) => {
        if (!organizationId.value) {
            return;
        }

        loading.value = true;
        failed.value = false;

        try {
            const served = await aiAssistantApi().propositions(organizationId.value, wanted);

            propositions.value = wanted === 1
                ? served.data
                : [...propositions.value, ...served.data];

            page.value = served.meta?.current_page ?? wanted;
            lastPage.value = served.meta?.last_page ?? wanted;
            hasMore.value = page.value < lastPage.value;
        } catch {
            failed.value = true;
        } finally {
            loading.value = false;
        }
    };

    const load = () => fetchPage(1);

    /** Read one page further back, keeping what is already on screen. */
    const loadMore = () => fetchPage(page.value + 1);

    // A decision taken from the conversation reaches an open panel.
    watch(revision, () => load());

    /**
     * Replay the inverse diff of an applied proposition.
     *
     * The undo is not a feature of its own: it is a consequence of the data
     * model, which recorded what would reverse the application at the moment
     * it was made. Here it is a call and a refresh of the screens showing the
     * hours it restores.
     */
    const cancel = async (proposition: Proposition): Promise<boolean> => {
        cancelling.value = proposition.id;
        failure.value = null;

        try {
            const cancelled = await aiAssistantApi().cancelProposition(proposition.id);

            propositions.value = propositions.value.map(
                candidate => candidate.id === cancelled.id ? cancelled : candidate
            );

            invalidatePlanningRange();

            return true;
        } catch (error) {
            const status = (error as AxiosError)?.response?.status ?? 0;

            failure.value = CANCEL_ERRORS[status] ?? DECISION_GENERIC_ERROR;

            return false;
        } finally {
            cancelling.value = null;
        }
    };

    return {propositions, loading, failed, hasMore, cancelling, failure, load, loadMore, cancel};
};

export default useAssistantHistory;
